import React from 'react';
import Page from "components/Page";
import {useNavigate, useParams} from "react-router-dom";
import AudienceSegmentForm from "pages/AudienceSegment/Form";
import {useMutation, useQuery} from "react-query";
import ngage from "services/ngage";
import {AudienceSegmentData} from "types/AudienceSegment";
import {useNotifications} from "context/NotificationContext";
import {invalidate} from "services/query-client";
import {FormikHelpers} from "formik";

function MutateAudienceSegment() {
    const {id} = useParams()
    const {data: audienceSegment, isLoading} = useQuery(
        ['audience-segments', {id}],
        () => ngage.audienceSegments.get(parseInt(id as string)),
        {enabled: !!id}
    )

    const store = useMutation((data: AudienceSegmentData) => ngage.audienceSegments.store(data))
    const update = useMutation((data: AudienceSegmentData) => ngage.audienceSegments.update(parseInt(id as string), data))

    const {notify} = useNotifications()
    const navigate = useNavigate()

    // Handle form submission
    async function handleSubmit(values: AudienceSegmentData, {setSubmitting}: FormikHelpers<AudienceSegmentData>) {
        id? await handleUpdate(values) : await handleStore(values)
        await invalidate('audience-segments.all')

        setSubmitting(false)
        navigate('/audience-segments')
    }

    // Handle store mutation
    async function handleStore(values: AudienceSegmentData) {
        try {
            await store.mutateAsync(values)
            notify("success", "Audience Segment Created", `${values.name} has been created`)
        } catch (error) {
            notify("error", "Audience Segment Not Created", `${values.name} could not be stored`)
            throw error
        }
    }

    // Handle update mutation
    async function handleUpdate(values: AudienceSegmentData) {
        try {
            await update.mutateAsync(values)
            notify("success", "Audience Segment Updated", `${values.name} has been updated`)
            await invalidate(['audience-segments', {id}])
        } catch (error) {
            notify("error", "Audience Segment Not Updated", `${values.name} could not be updated`)
            throw error
        }
    }

    // Prepare breadcrumbs
    const newBreadcrumbs = {'Engagement': '/', 'Segments': '/audience-segments', 'New Segment': '/audience-segments/new'}
    const editBreadcrumbs = {'Engagement': '/', 'Segments': '/audience-segments', 'Edit Segment': `/audience-segments/${id}/edit`}

    return (
        <Page title={id ? "Edit Segment" : "New Segment"}
              breadcrumbs={id ? editBreadcrumbs : newBreadcrumbs}
              isLoading={isLoading}>
            <AudienceSegmentForm audienceSegment={audienceSegment} onSubmit={handleSubmit}/>
        </Page>
    );
}

export default MutateAudienceSegment;